<template>
  <div class="sv-root">

    <!-- ══════════════════════ HEADER ══════════════════════════ -->
    <div class="sv-header">
      <div class="sv-header-top">
        <div>
          <p class="sv-eyebrow">Manager · MoMo Auto-validation</p>
          <h1 class="sv-title">SMS Validations</h1>
        </div>
        <button class="sv-icon-btn" :disabled="loading" @click="fetchAll">
          <v-icon small :class="{ spinning: loading }">mdi-refresh</v-icon>
        </button>
      </div>

      <div class="sv-stats">
        <div
          v-for="stat in statCards" :key="stat.key"
          class="sv-stat"
          :class="{ 'sv-stat-active': selectedStatus === stat.filter }"
          @click="stat.filter !== undefined && quickFilter(stat.filter)"
        >
          <span class="sv-stat-val" :class="stat.cls">{{ computedAgg[stat.key] }}</span>
          <span class="sv-stat-lbl">{{ stat.label }}</span>
        </div>
      </div>
    </div>

    <!-- ══════════════════════ FILTERS ══════════════════════════ -->
    <div class="sv-filters">
      <div class="sv-chip-row">
        <button
          v-for="s in statusOptions" :key="s.value"
          class="sv-chip" :class="[{ 'sv-chip-active': selectedStatus === s.value }, 'chip-' + s.color]"
          @click="selectedStatus = s.value; reload()"
        >{{ s.label }}</button>
      </div>

      <div class="sv-feat-row">
        <button
          v-for="f in featureOptions" :key="f.value"
          class="sv-feat-btn" :class="{ active: selectedFeature === f.value }"
          @click="selectedFeature = f.value; reload()"
        >
          <v-icon x-small class="mr-1">{{ f.icon }}</v-icon>{{ f.label }}
        </button>
      </div>

      <div class="sv-filter-bottom">
        <div class="sv-input-wrap" style="flex:2;min-width:160px">
          <v-icon small class="sv-input-ico">mdi-account-search-outline</v-icon>
          <input
            v-model="userSearch" class="sv-input" placeholder="Filter by client…"
            @input="onUserSearchInput" @blur="hideSuggestions"
          />
          <div class="sv-suggestions" v-if="userSuggestions.length">
            <div
              v-for="u in userSuggestions" :key="u.id"
              class="sv-suggestion" @mousedown.prevent="selectUser(u)"
            >
              <span class="su-name">{{ u.username }}</span>
              <span class="su-full">{{ u.full_name }}</span>
            </div>
          </div>
          <div v-if="selectedUser" class="sv-selected-user">
            {{ selectedUser.username }}
            <button class="su-clear" @click="clearUser">×</button>
          </div>
        </div>

        <select v-model="selectedOperator" class="sv-select" @change="reload()">
          <option value="">All operators</option>
          <option v-for="op in operatorOptions" :key="op" :value="op">{{ op }}</option>
        </select>

        <select v-model="selectedDays" class="sv-select" @change="reload()">
          <option v-for="d in dayOptions" :key="d.value" :value="d.value">{{ d.label }}</option>
        </select>

        <div class="sv-input-wrap" style="flex:2;min-width:160px">
          <v-icon small class="sv-input-ico">mdi-magnify</v-icon>
          <input v-model="searchQuery" class="sv-input" placeholder="Phone, ref, username…" @input="onQueryInput" />
        </div>
      </div>
    </div>

    <!-- ══════════════════════ LOADING / EMPTY ══════════════════ -->
    <div v-if="loading" class="sv-state">
      <v-progress-circular indeterminate color="primary" size="36" />
      <p class="sv-state-txt">Loading…</p>
    </div>

    <div v-else-if="clientGroups.length === 0 && unmatchedItems.length === 0" class="sv-state">
      <v-icon size="48" color="grey lighten-1">mdi-message-check-outline</v-icon>
      <p class="sv-state-txt">No validations found</p>
      <p class="sv-state-sub">Try adjusting filters or extending the time window.</p>
    </div>

    <!-- ══════════════════════ CONTENT ══════════════════════════ -->
    <div v-else class="sv-content">

      <!-- ── CLIENT GROUPS ──────────────────────────────────────── -->
      <template v-if="clientGroups.length">
        <div class="sv-section-label">
          <v-icon small color="#1a2a4a" class="mr-1">mdi-account-multiple-outline</v-icon>
          Clients ({{ clientGroups.length }})
        </div>

        <div v-for="group in clientGroups" :key="'c-' + group.username" class="sv-user-card">

          <div class="suc-header" @click="togglePicker(group.username)">
            <div class="suc-left">
              <div class="sv-avatar" :style="avatarStyle(group.username)">
                {{ initials(group.username) }}
              </div>
              <div class="suc-info">
                <span class="suc-name">{{ group.username }}</span>
                <div class="suc-badges">
                  <span v-if="group.counts.validated"  class="mini-badge badge-success">{{ group.counts.validated }} validated</span>
                  <span v-if="group.counts.partial"    class="mini-badge badge-warning">{{ group.counts.partial }} partial</span>
                  <span v-if="group.counts.failed"     class="mini-badge badge-error">{{ group.counts.failed }} failed</span>
                  <span v-if="group.counts.cancelled"  class="mini-badge badge-cancel">{{ group.counts.cancelled }} cancelled</span>
                </div>
              </div>
            </div>
            <div class="suc-right">
              <span class="suc-total">{{ group.items.length }} tx</span>
              <v-icon small :class="{ 'rot-180': pickerOpen === group.username }">mdi-chevron-down</v-icon>
            </div>
          </div>

          <transition name="slide-down">
            <div class="tx-picker" v-if="pickerOpen === group.username">
              <div class="tx-picker-header">
                <span class="tx-picker-hint">
                  <v-icon x-small class="mr-1">mdi-cursor-default-click-outline</v-icon>
                  Select a transaction to view details
                </span>
                <span class="tx-picker-count">{{ group.items.length }} transactions</span>
              </div>

              <div
                v-for="item in group.items" :key="item.id"
                class="tx-pick-row"
                :class="'pick-' + statusCls(item.status)"
                @click="openDetail(item)"
              >
                <div class="tpr-left">
                  <span class="feat-dot" :class="'fdot-' + featureColor(item.feature)"></span>
                  <div class="tpr-info">
                    <span class="tpr-type">{{ featureLabel(item.feature) }}</span>
                    <span class="tpr-date">
                      {{ fmtDateShort(item.created_at) }}
                      <span class="tpr-time">{{ fmtTime(item.created_at) }}</span>
                      <span class="op-badge" v-if="item.sms_operator">{{ item.sms_operator }}</span>
                    </span>
                  </div>
                </div>

                <div class="tpr-right">
                  <span class="tpr-amount" :class="amountCls(item)">
                    {{ fmtAmount(item.sms_amount) }}
                  </span>
                  <span class="sv-badge" :class="'badge-' + statusCls(item.status)">
                    {{ statusLabel(item.status) }}
                  </span>
                  <v-icon x-small color="grey lighten-1" class="ml-1">mdi-chevron-right</v-icon>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </template>

      <!-- ── UNMATCHED SMS ──────────────────────────────────────── -->
      <template v-if="unmatchedItems.length && (selectedStatus === '' || selectedStatus === 'no_match')">
        <div class="sv-section-label sv-section-unmatched">
          <v-icon small color="#92400e" class="mr-1">mdi-cellphone-off</v-icon>
          Unmatched SMS ({{ unmatchedItems.length }})
          <span class="unmatched-hint">Received but matched no pending request.</span>
        </div>

        <div class="sv-unmatched-card">
          <div
            v-for="item in unmatchedItems" :key="item.id"
            class="unm-row"
            @click="openDetail(item)"
          >
            <div class="unm-left">
              <div class="unm-device-icon">
                <v-icon small color="#92400e">mdi-cellphone-text</v-icon>
              </div>
              <div class="unm-info">
                <div class="unm-feat">
                  <span class="feat-dot" :class="'fdot-' + featureColor(item.feature)"></span>
                  <span class="feat-label">{{ featureLabel(item.feature) }}</span>
                  <span class="unm-device-tag">
                    <v-icon x-small>mdi-sim-outline</v-icon>
                    {{ item.username }}
                  </span>
                </div>
                <div class="suc-meta">
                  <span class="meta-date">{{ fmtDateShort(item.created_at) }} {{ fmtTime(item.created_at) }}</span>
                  <span class="meta-phone" v-if="item.sms_phone">
                    <v-icon x-small>mdi-phone-outline</v-icon> {{ item.sms_phone }}
                  </span>
                  <span class="op-badge" v-if="item.sms_operator">{{ item.sms_operator }}</span>
                </div>
              </div>
            </div>
            <div class="unm-right">
              <span class="amt-sms">{{ fmtAmount(item.sms_amount) }}</span>
              <v-icon x-small color="grey lighten-1">mdi-chevron-right</v-icon>
            </div>
          </div>
        </div>
      </template>

    </div>

    <!-- ══════════════════════ DETAIL DIALOG ═══════════════════ -->
    <v-dialog v-model="detailDialog" max-width="640" scrollable>
      <v-card class="detail-card" v-if="selected">
        <div class="dc-head" :class="'dch-' + featureColor(selected.feature)">
          <div class="dc-head-left">
            <div class="dc-head-icon">
              <v-icon color="white" small>{{ featureIcon(selected.feature) }}</v-icon>
            </div>
            <div>
              <p class="dc-head-type">{{ featureLabel(selected.feature) }}</p>
              <p class="dc-head-id">Validation #{{ selected.id }}</p>
            </div>
          </div>
          <span class="sv-badge" :class="'badge-' + statusCls(selected.status)">
            {{ statusLabel(selected.status) }}
          </span>
        </div>

        <v-card-text class="dc-body">
          <div class="dc-section">
            <p class="dc-sec-title">{{ selected.status === 'no_match' ? 'Device Owner (No Client)' : 'Client' }}</p>
            <div class="dc-user-row">
              <div v-if="selected.status === 'no_match'" class="unm-device-icon lg">
                <v-icon color="#92400e">mdi-cellphone-text</v-icon>
              </div>
              <div v-else class="sv-avatar lg" :style="avatarStyle(selected.username)">{{ initials(selected.username) }}</div>
              <div>
                <p class="dc-uname">{{ selected.username }}</p>
                <p class="dc-umodel" v-if="selected.status !== 'no_match'">{{ selected.request_model }} #{{ selected.request_id }}</p>
                <p class="dc-umodel warn-text" v-else>No pending request matched this SMS</p>
              </div>
            </div>
          </div>

          <div class="dc-section">
            <p class="dc-sec-title">Amounts</p>
            <div class="dc-amt-grid">
              <div class="dc-amt-box">
                <span class="dab-lbl">SMS received</span>
                <span class="dab-val green-val">{{ fmtAmount(selected.sms_amount) }}</span>
              </div>
              <div class="dc-amt-box" v-if="selected.request_amount">
                <span class="dab-lbl">Requested</span>
                <span class="dab-val">{{ fmtAmount(selected.request_amount) }}</span>
              </div>
              <div class="dc-amt-box" v-if="selected.status === 'partial'">
                <span class="dab-lbl">Outstanding</span>
                <span class="dab-val amber-val">{{ fmtAmount(selected.remainder_amount) }}</span>
              </div>
            </div>
          </div>

          <div class="dc-section">
            <p class="dc-sec-title">SMS Details</p>
            <table class="dc-table">
              <tr><td class="dc-lbl">Phone</td><td class="dc-val mono">{{ selected.sms_phone || '—' }}</td></tr>
              <tr><td class="dc-lbl">Operator</td><td class="dc-val">{{ selected.sms_operator || '—' }}</td></tr>
              <tr v-if="selected.match_score !== null && selected.match_score !== undefined">
                <td class="dc-lbl">Match score</td>
                <td class="dc-val">
                  <div class="score-row">
                    <div class="score-bar-wrap"><div class="score-bar" :style="scoreBarStyle(selected.match_score)"></div></div>
                    <span class="score-num">{{ Number(selected.match_score).toFixed(1) }}</span>
                  </div>
                </td>
              </tr>
              <tr><td class="dc-lbl">Validated by</td><td class="dc-val">{{ selected.validated_by || '—' }}</td></tr>
              <tr v-if="selected.cancelled_by"><td class="dc-lbl">Cancelled by</td><td class="dc-val">{{ selected.cancelled_by }}</td></tr>
              <tr><td class="dc-lbl">Date</td><td class="dc-val">{{ fmtDateFull(selected.created_at) }}</td></tr>
            </table>
          </div>

          <div class="dc-error-block" v-if="selected.error_message">
            <v-icon small color="red darken-1">mdi-alert-circle-outline</v-icon>
            {{ selected.error_message }}
          </div>

          <div class="dc-section" v-if="selected.sms_body">
            <p class="dc-sec-title">SMS Body</p>
            <pre class="dc-sms-pre">{{ selected.sms_body }}</pre>
          </div>

          <div class="dc-section">
            <p class="dc-sec-title">Manual match</p>
            <div class="dc-manual">
              <input v-model="manualModel" class="sv-input" placeholder="e.g. client.RequestSaveTransaction" style="flex:2;padding-left:10px" />
              <input v-model="manualId"    class="sv-input" placeholder="ID" type="number" style="flex:1;padding-left:10px;max-width:90px" />
              <button class="sv-btn btn-primary" :disabled="loading || !manualModel || !manualId" @click="manualMatch">
                Associate
              </button>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="dc-footer">
          <button class="sv-btn btn-success" :disabled="!canRetry(selected) || loading" @click="openRetryDialog(selected)">
            <v-icon x-small class="mr-1">mdi-reload</v-icon> Retry
          </button>
          <button class="sv-btn btn-danger" :disabled="!canCancel(selected) || loading" @click="openCancelDialog(selected)">
            <v-icon x-small class="mr-1">mdi-close-circle-outline</v-icon> Cancel
          </button>
          <v-spacer />
          <button class="sv-btn btn-ghost" @click="detailDialog = false">Close</button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══════════════════════ CANCEL DIALOG ═══════════════════ -->
    <v-dialog v-model="cancelDialog" max-width="420" persistent>
      <v-card class="cancel-card" v-if="cancelTarget">
        <div class="cc-head" style="background:#b91c1c">
          <div class="cc-icon-wrap"><v-icon color="white" size="22">mdi-alert-circle-outline</v-icon></div>
          <div>
            <p class="cc-title">Cancel validation?</p>
            <p class="cc-sub">This cannot be undone.</p>
          </div>
        </div>
        <div class="cc-body">
          <div class="cc-row"><span class="cc-lbl">Client</span><span class="cc-val">{{ cancelTarget.username }}</span></div>
          <div class="cc-row"><span class="cc-lbl">Type</span><span class="cc-val">{{ featureLabel(cancelTarget.feature) }}</span></div>
          <div class="cc-row"><span class="cc-lbl">Amount</span><span class="cc-val">{{ fmtAmount(cancelTarget.sms_amount) }}</span></div>
          <div class="cc-row"><span class="cc-lbl">Request</span><span class="cc-val">{{ cancelTarget.request_model }} #{{ cancelTarget.request_id }}</span></div>
          <div class="cc-warning">The linked transaction will be reset to pending. Verify manually after cancellation.</div>
        </div>
        <div class="cc-footer">
          <button class="sv-btn btn-ghost" @click="cancelDialog = false" :disabled="loading">Keep it</button>
          <button class="sv-btn btn-danger-solid" @click="doCancel" :disabled="loading">
            <v-progress-circular v-if="loading" indeterminate size="14" width="2" color="white" class="mr-1" />
            <v-icon v-else x-small class="mr-1">mdi-close</v-icon>
            Yes, cancel
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- ══════════════════════ RETRY DIALOG ════════════════════ -->
    <v-dialog v-model="retryDialog" max-width="420" persistent>
      <v-card class="cancel-card" v-if="retryTarget">
        <div class="cc-head" style="background:#0F6E56">
          <div class="cc-icon-wrap"><v-icon color="white" size="22">mdi-reload</v-icon></div>
          <div>
            <p class="cc-title">Retry validation?</p>
            <p class="cc-sub">A new attempt will be made.</p>
          </div>
        </div>
        <div class="cc-body">
          <div class="cc-row"><span class="cc-lbl">Client / Device</span><span class="cc-val">{{ retryTarget.username }}</span></div>
          <div class="cc-row"><span class="cc-lbl">Type</span><span class="cc-val">{{ featureLabel(retryTarget.feature) }}</span></div>
          <div class="cc-row"><span class="cc-lbl">Amount</span><span class="cc-val">{{ fmtAmount(retryTarget.sms_amount) }}</span></div>
          <div class="cc-row">
            <span class="cc-lbl">Status</span>
            <span class="sv-badge" :class="'badge-' + statusCls(retryTarget.status)">{{ statusLabel(retryTarget.status) }}</span>
          </div>
        </div>
        <div class="cc-footer">
          <button class="sv-btn btn-ghost" @click="retryDialog = false" :disabled="loading">Cancel</button>
          <button class="sv-btn btn-success-solid" @click="doRetry" :disabled="loading">
            <v-progress-circular v-if="loading" indeterminate size="14" width="2" color="white" class="mr-1" />
            <v-icon v-else x-small class="mr-1">mdi-reload</v-icon>
            Yes, retry
          </button>
        </div>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import axios from 'axios'

const FEATURE_META = {
  savings_campaign:       { label: 'Savings – Deposit',     icon: 'mdi-piggy-bank-outline',   color: 'teal'   },
  savings_withdrawal:     { label: 'Savings – Withdrawal',  icon: 'mdi-cash-minus',            color: 'orange' },
  savings_interrupt:      { label: 'Savings – Interrupted', icon: 'mdi-stop-circle-outline',   color: 'orange' },
  savings_loan_deposit:   { label: 'Loan – Deposit',        icon: 'mdi-bank-outline',          color: 'blue'   },
  savings_loan_repayment: { label: 'Loan – Repayment',      icon: 'mdi-bank-transfer-in',      color: 'blue'   },
  njangi:                 { label: 'Njangi',                icon: 'mdi-account-group-outline', color: 'purple' },
  marketplace:            { label: 'Marketplace',           icon: 'mdi-store-outline',         color: 'pink'   },
  investment:             { label: 'Investment',            icon: 'mdi-chart-line',            color: 'green'  },
  business_account:       { label: 'Business Account',      icon: 'mdi-briefcase-outline',     color: 'indigo' },
}

const AVATAR_COLORS = ['#1d3461','#0F6E56','#534AB7','#993556','#3B6D11','#185FA5','#E65100','#283593','#6d1a36']
function hashColor(str) {
  var s = str || '', h = 0
  for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

export default {
  name: 'SmsValidationManager',

  data: function () {
    return {
      loading: false,
      items: [],
      pickerOpen: null,
      selectedStatus:   '',
      selectedFeature:  '',
      selectedOperator: '',
      selectedDays:     90,
      searchQuery:      '',
      userSearch:      '',
      selectedUser:    null,
      userSuggestions: [],
      userSearchTimer: null,
      detailDialog: false,
      selected:     null,
      manualModel:  '',
      manualId:     '',
      cancelDialog: false,
      cancelTarget: null,
      retryDialog:  false,
      retryTarget:  null,
      queryTimer: null,

      statCards: [
        { key: 'total',     label: 'Total',     cls: 'val-white', filter: undefined   },
        { key: 'validated', label: 'Validated', cls: 'val-green', filter: 'validated' },
        { key: 'partial',   label: 'Partial',   cls: 'val-amber', filter: 'partial'   },
        { key: 'failed',    label: 'Failed',    cls: 'val-red',   filter: 'failed'    },
        { key: 'no_match',  label: 'Unmatched', cls: 'val-gray',  filter: 'no_match'  },
        { key: 'cancelled', label: 'Cancelled', cls: 'val-red',   filter: 'cancelled' },
      ],

      statusOptions: [
        { value: '',          label: 'All',       color: 'neutral' },
        { value: 'validated', label: 'Validated', color: 'success' },
        { value: 'partial',   label: 'Partial',   color: 'warning' },
        { value: 'failed',    label: 'Failed',    color: 'error'   },
        { value: 'no_match',  label: 'Unmatched', color: 'muted'   },
        { value: 'cancelled', label: 'Cancelled', color: 'cancel'  },
      ],

      featureOptions: [
        { value: '',                      label: 'All',        icon: 'mdi-view-list'              },
        { value: 'savings_campaign',      label: 'Savings',    icon: 'mdi-piggy-bank-outline'     },
        { value: 'savings_loan_repayment',label: 'Loans',      icon: 'mdi-bank-outline'           },
        { value: 'njangi',                label: 'Njangi',     icon: 'mdi-account-group-outline'  },
        { value: 'marketplace',           label: 'Marketplace',icon: 'mdi-store-outline'          },
        { value: 'investment',            label: 'Investment', icon: 'mdi-chart-line'             },
        { value: 'business_account',      label: 'Business',   icon: 'mdi-briefcase-outline'      },
      ],

      operatorOptions: ['MTN_MOMO', 'ORANGE_MONEY', 'AIRTEL', 'YONDO'],

      dayOptions: [
        { label: 'Last 7 days',   value: 7   },
        { label: 'Last 30 days',  value: 30  },
        { label: 'Last 90 days',  value: 90  },
        { label: 'Last 6 months', value: 180 },
        { label: 'Last year',     value: 365 },
      ],
    }
  },

  computed: {
    computedAgg: function () {
      var counts = { total: 0, validated: 0, partial: 0, failed: 0, no_match: 0, cancelled: 0 }
      this.items.forEach(function (i) {
        counts.total++
        if (counts[i.status] !== undefined) counts[i.status]++
      })
      return counts
    },

    clientItems: function () {
      return this.items.filter(function (i) { return i.status !== 'no_match' })
    },

    unmatchedItems: function () {
      return this.items.filter(function (i) { return i.status === 'no_match' })
    },

    clientGroups: function () {
      var groups = {}
      this.clientItems.forEach(function (item) {
        var key = item.username || '—'
        if (!groups[key]) {
          groups[key] = {
            username: key,
            items: [],
            counts: { validated: 0, partial: 0, failed: 0, cancelled: 0 },
          }
        }
        groups[key].items.push(item)
        var s = item.status
        if (groups[key].counts[s] !== undefined) groups[key].counts[s]++
      })
      return Object.values(groups).sort(function (a, b) {
        var aDate = (a.items[0] && a.items[0].created_at) || ''
        var bDate = (b.items[0] && b.items[0].created_at) || ''
        return bDate < aDate ? -1 : bDate > aDate ? 1 : 0
      })
    },
  },

  mounted: function () { this.fetchAll() },

  methods: {
    authHeaders: function () {
      var token = this.$store && this.$store.state && this.$store.state.token
      return token ? { headers: { Authorization: 'Token ' + token } } : {}
    },

    fetchAll: async function () {
      this.loading  = true
      this.pickerOpen = null
      try {
        var params = {
          status:   this.selectedStatus   || undefined,
          feature:  this.selectedFeature  || undefined,
          operator: this.selectedOperator || undefined,
          q:        this.searchQuery      || undefined,
          days:     this.selectedDays,
          limit:    500,
        }
        if (this.selectedUser) params.user_id = this.selectedUser.id

        var resp = await axios.get(
          '/api/v1/manager/communication/sms-validations/',
          Object.assign({}, this.authHeaders(), { params: params })
        )
        var data = resp.data
        this.items = Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : [])

      } catch (e) {
        console.error('[SmsValidation] fetch error:', e)
        this._snack('Failed to load SMS validations', 'red darken-2')
      } finally {
        this.loading = false
      }
    },

    reload: function () { this.fetchAll() },

    quickFilter: function (status) {
      this.selectedStatus = (this.selectedStatus === status) ? '' : status
      this.reload()
    },

    togglePicker: function (username) {
      this.pickerOpen = (this.pickerOpen === username) ? null : username
    },

    onUserSearchInput: function (e) {
      clearTimeout(this.userSearchTimer)
      var val = (e && e.target) ? e.target.value : e
      if (!val || val.length < 2) { this.userSuggestions = []; return }
      var self = this
      this.userSearchTimer = setTimeout(function () { self.searchUsers(val) }, 350)
    },

    searchUsers: async function (q) {
      try {
        var resp = await axios.get('/api/v1/manager/users/', Object.assign({}, this.authHeaders(), { params: { q: q, limit: 8 } }))
        var data = resp.data
        this.userSuggestions = (data.results || data || []).map(function (u) {
          return { id: u.id, username: u.username, full_name: ((u.first_name || '') + ' ' + (u.last_name || '')).trim() }
        })
      } catch (e) { this.userSuggestions = [] }
    },

    selectUser:      function (u) { this.selectedUser = u; this.userSearch = u.username; this.userSuggestions = []; this.reload() },
    clearUser:       function ()  { this.selectedUser = null; this.userSearch = ''; this.userSuggestions = []; this.reload() },
    hideSuggestions: function ()  { var self = this; setTimeout(function () { self.userSuggestions = [] }, 200) },

    onQueryInput: function () {
      clearTimeout(this.queryTimer)
      var self = this
      this.queryTimer = setTimeout(function () { self.reload() }, 400)
    },

    openDetail: function (item) {
      this.selected     = Object.assign({}, item)
      this.manualModel  = item.request_model || ''
      this.manualId     = item.request_id    || ''
      this.detailDialog = true
    },

    openCancelDialog: function (item) { this.cancelTarget = item; this.cancelDialog = true },
    openRetryDialog:  function (item) { this.retryTarget  = item; this.retryDialog  = true },

    doCancel: async function () {
      if (!this.cancelTarget) return
      this.loading = true
      try {
        await axios.post(
          '/api/v1/manager/communication/sms-validations/' + this.cancelTarget.id + '/cancel/',
          {}, this.authHeaders()
        )
        this._snack('Validation cancelled', 'orange darken-2')
        this.cancelDialog = false; this.detailDialog = false; this.cancelTarget = null
        await this.fetchAll()
      } catch (e) {
        this._snack((e.response && e.response.data && e.response.data.detail) || 'Cancellation failed', 'red darken-2')
      } finally { this.loading = false }
    },

    doRetry: async function () {
      if (!this.retryTarget) return
      this.loading = true
      try {
        await axios.post(
          '/api/v1/manager/communication/sms-validations/' + this.retryTarget.id + '/retry/',
          {}, this.authHeaders()
        )
        this._snack('Retry launched', 'green darken-2')
        this.retryDialog = false; this.detailDialog = false; this.retryTarget = null
        await this.fetchAll()
      } catch (e) {
        this._snack((e.response && e.response.data && e.response.data.detail) || 'Retry failed', 'red darken-2')
      } finally { this.loading = false }
    },

    manualMatch: async function () {
      if (!this.selected || !this.manualModel || !this.manualId) return
      this.loading = true
      try {
        var resp = await axios.post(
          '/api/v1/manager/communication/sms-validations/' + this.selected.id + '/match/',
          { content_type: this.manualModel, object_id: this.manualId },
          this.authHeaders()
        )
        this.selected = Object.assign({}, this.selected, resp.data)
        this._snack('Match applied', 'green darken-2')
        await this.fetchAll()
      } catch (e) {
        this._snack((e.response && e.response.data && e.response.data.detail) || 'Match failed', 'red darken-2')
      } finally { this.loading = false }
    },

    canRetry:  function (item) { return item && (item.status === 'failed' || item.status === 'no_match') },
    canCancel: function (item) { return item && (item.status === 'validated' || item.status === 'partial') },

    featureLabel: function (f) { return (FEATURE_META[f] && FEATURE_META[f].label) || f || '—' },
    featureIcon:  function (f) { return (FEATURE_META[f] && FEATURE_META[f].icon)  || 'mdi-cash' },
    featureColor: function (f) { return (FEATURE_META[f] && FEATURE_META[f].color) || 'teal' },

    statusCls: function (s) {
      var m = { validated: 'success', partial: 'warning', failed: 'error', no_match: 'muted', cancelled: 'cancel' }
      return m[s] || 'muted'
    },
    statusLabel: function (s) {
      var m = { validated: 'Validated', partial: 'Partial', failed: 'Failed', no_match: 'Unmatched', cancelled: 'Cancelled' }
      return m[s] || s || '—'
    },
    amountCls: function (item) {
      if (item.status === 'failed' || item.status === 'cancelled') return 'amt-cancel'
      if (item.status === 'partial') return 'amt-partial'
      if (item.status === 'validated') return 'amt-ok'
      return ''
    },

    scoreBarStyle: function (score) {
      var pct = Math.max(0, Math.min(100, 100 - (score / 500)))
      var color = pct > 70 ? '#22c55e' : pct > 40 ? '#f59e0b' : '#ef4444'
      return { width: pct + '%', background: color }
    },

    fmtAmount: function (val) {
      if (val === null || val === undefined) return '—'
      return (Number(val) || 0).toLocaleString('fr-FR') + ' FCFA'
    },
    fmtDateShort: function (iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    },
    fmtTime: function (iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    },
    fmtDateFull: function (iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    initials:    function (u) { return ((u || '?').slice(0, 2)).toUpperCase() },
    avatarStyle: function (u) { return { background: hashColor(u) } },

    _snack: function (msg, color) {
      if (!this.$store) return
      this.$store.state.snackBar        = true
      this.$store.state.snackBarColor   = color || 'red darken-2'
      this.$store.state.snackBarMessage = msg
    },
  },
}
</script>

<style scoped>
.sv-root{background:#f0f2f6;min-height:100vh;font-family:'Segoe UI',Arial,sans-serif;}

/* ── Header ─────────────────────────────────────────────────────────────── */
.sv-header{background:#1a2a4a;padding:14px 16px 16px;top:0;z-index:4;}
.sv-header-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px;}
.sv-eyebrow{color:#7ca3d4;font-size:10px;text-transform:uppercase;letter-spacing:1.2px;margin:0;}
.sv-title{color:#fff;font-size:17px;font-weight:700;margin:2px 0 0;}
.sv-icon-btn{background:rgba(255,255,255,.1);border:none;border-radius:8px;width:32px;height:32px;cursor:pointer;color:#fff;display:flex;align-items:center;justify-content:center;}
.sv-icon-btn:disabled{opacity:.4;cursor:default;}
@keyframes spin{to{transform:rotate(360deg);}}.spinning{animation:spin .75s linear infinite;}

.sv-stats{display:grid;grid-template-columns:repeat(6,1fr);gap:6px;}
@media(max-width:480px){.sv-stats{grid-template-columns:repeat(3,1fr);}}
.sv-stat{background:rgba(255,255,255,.07);border-radius:8px;padding:7px 4px;display:flex;flex-direction:column;align-items:center;cursor:pointer;transition:background .15s;border:1.5px solid transparent;}
.sv-stat:hover{background:rgba(255,255,255,.13);}
.sv-stat-active{background:rgba(255,255,255,.15);border-color:rgba(255,255,255,.3);}
.sv-stat-val{font-size:15px;font-weight:700;}
.sv-stat-lbl{font-size:9px;color:#7ca3d4;margin-top:2px;text-transform:uppercase;letter-spacing:.4px;text-align:center;}
.val-white{color:#e2e8f0;}.val-green{color:#6ee7b7;}.val-amber{color:#fcd34d;}.val-red{color:#fca5a5;}.val-gray{color:#94a3b8;}

/* ── Filters ────────────────────────────────────────────────────────────── */
.sv-filters{background:#fff;padding:10px 14px 10px;border-bottom:1px solid #e2e8f0;}
.sv-filters{display:flex;flex-direction:column;gap:8px;overflow:visible;}
.sv-chip-row{display:flex;gap:5px;flex-wrap:nowrap;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;}
.sv-chip-row::-webkit-scrollbar{display:none;}
.sv-feat-row{display:flex;gap:5px;flex-wrap:nowrap;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;padding-bottom:2px;}
.sv-feat-row::-webkit-scrollbar{display:none;}
.sv-chip{padding:4px 11px;border-radius:14px;font-size:11px;font-weight:500;border:1px solid #dee2e9;background:transparent;color:#64748b;cursor:pointer;white-space:nowrap;flex-shrink:0;}
.sv-chip-active.chip-neutral{background:#e2e8f0;border-color:#94a3b8;color:#334155;}
.sv-chip-active.chip-success{background:#dcfce7;border-color:#22c55e;color:#15803d;}
.sv-chip-active.chip-warning{background:#fef9c3;border-color:#eab308;color:#92400e;}
.sv-chip-active.chip-error{background:#fee2e2;border-color:#f87171;color:#b91c1c;}
.sv-chip-active.chip-muted{background:#f1f5f9;border-color:#94a3b8;color:#475569;}
.sv-chip-active.chip-cancel{background:#fef2f2;border-color:#fca5a5;color:#9f1239;}
.sv-feat-btn{display:flex;align-items:center;flex-shrink:0;white-space:nowrap;padding:5px 11px;border-radius:20px;font-size:11px;font-weight:500;border:1px solid #dee2e9;background:transparent;color:#64748b;cursor:pointer;}
.sv-feat-btn.active{background:#1a2a4a;color:#fff;border-color:#1a2a4a;}
.sv-filter-bottom{display:flex;gap:8px;flex-wrap:wrap;}
.sv-filter-bottom>*{flex:1 1 140px;}
.sv-input-wrap{position:relative;}
.sv-input-ico{position:absolute;left:9px;top:50%;transform:translateY(-50%);color:#94a3b8;pointer-events:none;z-index:1;}
.sv-input{width:100%;height:36px;padding:0 10px 0 32px;box-sizing:border-box;border:1px solid #dee2e9;border-radius:8px;font-size:12px;color:#1e293b;outline:none;}
.sv-input:focus{border-color:#185FA5;}
.sv-input::placeholder{color:#94a3b8;}
.sv-select{height:36px;padding:0 8px;border:1px solid #dee2e9;border-radius:8px;font-size:12px;color:#1e293b;background:#fff;cursor:pointer;outline:none;}
.sv-suggestions{position:absolute;top:calc(100% + 4px);left:0;right:0;z-index:100;background:#fff;border:1px solid #e2e8f0;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,.1);overflow:hidden;}
.sv-suggestion{display:flex;align-items:center;gap:8px;padding:9px 12px;cursor:pointer;border-bottom:1px solid #f1f5f9;}
.sv-suggestion:hover{background:#f8fafc;}
.su-name{font-size:13px;font-weight:600;color:#1e293b;}.su-full{font-size:11px;color:#94a3b8;}
.sv-selected-user{display:flex;align-items:center;gap:4px;position:absolute;right:8px;top:50%;transform:translateY(-50%);background:#e0f2fe;color:#0369a1;font-size:11px;font-weight:600;padding:2px 7px;border-radius:12px;}
.su-clear{background:none;border:none;cursor:pointer;color:#0369a1;font-size:14px;line-height:1;}
/* ── Empty/loading ──────────────────────────────────────────────────────── */
.sv-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 24px;gap:10px;}
.sv-state-txt{color:#64748b;font-size:15px;margin:0;}.sv-state-sub{color:#94a3b8;font-size:13px;margin:0;text-align:center;}

/* ── Content ────────────────────────────────────────────────────────────── */
.sv-content{padding:12px 12px 32px;display:flex;flex-direction:column;gap:10px;}

.sv-section-label{display:flex;align-items:center;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#1a2a4a;padding:2px 4px;gap:4px;flex-wrap:wrap;}
.sv-section-unmatched{color:#92400e;}
.unmatched-hint{font-size:10px;font-weight:400;text-transform:none;letter-spacing:0;color:#94a3b8;display:block;width:100%;margin-top:1px;}

/* ── User card ──────────────────────────────────────────────────────────── */
.sv-user-card{background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,.07);}
.suc-header{display:flex;align-items:center;justify-content:space-between;padding:12px 14px;cursor:pointer;transition:background .12s;}
.suc-header:hover{background:#f8fafc;}
.suc-left{display:flex;align-items:center;gap:10px;min-width:0;}
.sv-avatar{width:34px;height:34px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff;}
.sv-avatar.lg{width:40px;height:40px;font-size:13px;}
.suc-info{min-width:0;}
.suc-name{display:block;font-size:13px;font-weight:700;color:#1e293b;}
.suc-badges{display:flex;gap:4px;flex-wrap:wrap;margin-top:4px;}
.suc-right{display:flex;align-items:center;gap:8px;flex-shrink:0;margin-left:8px;}
.suc-total{font-size:12px;font-weight:700;color:#64748b;}
.rot-180{transform:rotate(180deg);transition:transform .2s;}

/* ── Transaction Picker ─────────────────────────────────────────────────── */
.tx-picker{border-top:1px solid #f1f5f9;}
.tx-picker-header{display:flex;justify-content:space-between;align-items:center;padding:8px 14px;background:#f8fafc;border-bottom:1px solid #f1f5f9;}
.tx-picker-hint{font-size:10px;color:#94a3b8;display:flex;align-items:center;}
.tx-picker-count{font-size:10px;font-weight:600;color:#64748b;}
.tx-pick-row{display:flex;align-items:center;justify-content:space-between;padding:10px 14px;gap:10px;cursor:pointer;border-bottom:1px solid #f8fafc;border-left:3px solid transparent;transition:background .12s, border-left-color .12s;}
.tx-pick-row:last-child{border-bottom:none;}
.tx-pick-row:hover{background:#f8fafc;}
.pick-success{border-left-color:#22c55e;}
.pick-warning{border-left-color:#f59e0b;}
.pick-error  {border-left-color:#ef4444;}
.pick-muted  {border-left-color:#cbd5e1;}
.pick-cancel {border-left-color:#fca5a5;}
.tpr-left{display:flex;align-items:center;gap:8px;min-width:0;flex:1;}
.tpr-info{display:flex;flex-direction:column;gap:2px;min-width:0;}
.tpr-type{font-size:12px;font-weight:600;color:#1e293b;}
.tpr-date{font-size:11px;color:#94a3b8;display:flex;align-items:center;gap:5px;flex-wrap:wrap;}
.tpr-time{color:#b0b8c8;}
.tpr-right{display:flex;align-items:center;gap:7px;flex-shrink:0;}
.tpr-amount{font-size:13px;font-weight:700;}
.slide-down-enter-active,.slide-down-leave-active{transition:all .2s ease;}
.slide-down-enter{opacity:0;transform:translateY(-6px);}
.slide-down-leave-to{opacity:0;transform:translateY(-6px);}

/* ── Feature dot ────────────────────────────────────────────────────────── */
.feat-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;}
.fdot-teal{background:#0F6E56;}.fdot-orange{background:#E65100;}.fdot-blue{background:#185FA5;}
.fdot-purple{background:#534AB7;}.fdot-pink{background:#993556;}.fdot-green{background:#3B6D11;}.fdot-indigo{background:#283593;}
.feat-label{font-size:12px;font-weight:600;color:#334155;}
.op-badge{font-size:10px;background:#f1f5f9;color:#475569;border-radius:4px;padding:1px 6px;}
.suc-meta{display:flex;gap:8px;align-items:center;flex-wrap:wrap;}
.meta-date{font-size:11px;color:#94a3b8;}
.meta-phone{font-size:11px;color:#64748b;font-family:'Courier New',monospace;display:flex;align-items:center;gap:2px;}
.amt-sms{font-size:13px;font-weight:700;}
.amt-ok{color:#15803d;}.amt-partial{color:#b45309;}.amt-cancel{color:#94a3b8;text-decoration:line-through;}

/* ── Unmatched section ──────────────────────────────────────────────────── */
.sv-unmatched-card{background:#fffbeb;border:1px solid #fde68a;border-radius:12px;overflow:hidden;}
.unm-row{display:flex;justify-content:space-between;align-items:flex-start;gap:10px;padding:11px 14px;border-bottom:1px solid #fef3c7;cursor:pointer;transition:background .12s;}
.unm-row:last-child{border-bottom:none;}
.unm-row:hover{background:#fef9e7;}
.unm-left{display:flex;align-items:flex-start;gap:10px;min-width:0;flex:1;}
.unm-device-icon{width:34px;height:34px;border-radius:10px;background:#fef3c7;border:1px solid #fde68a;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.unm-device-icon.lg{width:40px;height:40px;border-radius:12px;}
.unm-info{min-width:0;display:flex;flex-direction:column;gap:3px;}
.unm-feat{display:flex;align-items:center;gap:6px;flex-wrap:wrap;}
.unm-device-tag{display:inline-flex;align-items:center;gap:3px;font-size:10px;color:#92400e;background:#fef3c7;padding:2px 7px;border-radius:8px;border:1px solid #fde68a;font-weight:600;}
.unm-right{display:flex;align-items:center;gap:6px;flex-shrink:0;}

/* ── Badges ─────────────────────────────────────────────────────────────── */
.sv-badge{font-size:10px;font-weight:600;border-radius:7px;padding:3px 8px;white-space:nowrap;}
.badge-success{background:#dcfce7;color:#15803d;}.badge-warning{background:#fef9c3;color:#92400e;}
.badge-error{background:#fee2e2;color:#b91c1c;}.badge-muted{background:#f1f5f9;color:#475569;}.badge-cancel{background:#fef2f2;color:#9f1239;}
.mini-badge{font-size:10px;font-weight:600;border-radius:6px;padding:2px 7px;}
.mini-badge.badge-success{background:#dcfce7;color:#15803d;}.mini-badge.badge-warning{background:#fef9c3;color:#92400e;}
.mini-badge.badge-error{background:#fee2e2;color:#b91c1c;}.mini-badge.badge-cancel{background:#fef2f2;color:#9f1239;}

/* ── Generic buttons ────────────────────────────────────────────────────── */
.sv-btn{padding:7px 14px;border-radius:8px;font-size:12px;font-weight:600;border:1px solid transparent;cursor:pointer;display:inline-flex;align-items:center;}
.sv-btn:disabled{opacity:.4;cursor:default;}
.btn-primary{background:#185FA5;color:#fff;border-color:#185FA5;}.btn-primary:not(:disabled):hover{background:#0c447c;}
.btn-ghost{background:transparent;color:#64748b;border-color:#dee2e9;}.btn-ghost:hover{background:#f1f5f9;}
.btn-danger{border-color:#fecaca;color:#dc2626;background:transparent;}.btn-danger:not(:disabled):hover{background:#fee2e2;}
.btn-success{border-color:#bbf7d0;color:#16a34a;background:transparent;}.btn-success:not(:disabled):hover{background:#dcfce7;}
.btn-danger-solid{background:#dc2626;color:#fff;border-color:#dc2626;}.btn-danger-solid:not(:disabled):hover{background:#b91c1c;}
.btn-success-solid{background:#15803d;color:#fff;border-color:#15803d;}.btn-success-solid:not(:disabled):hover{background:#0F6E56;}

/* ── Detail dialog ──────────────────────────────────────────────────────── */
.detail-card{border-radius:12px !important;overflow:hidden;}
.dc-head{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;}
.dc-head-left{display:flex;align-items:center;gap:12px;}
.dc-head-icon{width:38px;height:38px;border-radius:10px;background:rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.dc-head-type{margin:0;font-size:10px;color:rgba(255,255,255,.7);}
.dc-head-id{margin:2px 0 0;font-size:14px;font-weight:700;color:#fff;}
.dch-teal{background:#0F6E56;}.dch-orange{background:#E65100;}.dch-blue{background:#185FA5;}
.dch-purple{background:#534AB7;}.dch-pink{background:#993556;}.dch-green{background:#3B6D11;}.dch-indigo{background:#283593;}
.dc-body{padding:14px 18px !important;background:#fff;}
.dc-section{margin-bottom:16px;}
.dc-sec-title{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin:0 0 8px;}
.dc-user-row{display:flex;align-items:center;gap:12px;}
.dc-uname{font-size:14px;font-weight:700;color:#1e293b;margin:0;}
.dc-umodel{font-size:11px;color:#94a3b8;margin:2px 0 0;font-family:monospace;}
.warn-text{color:#92400e !important;}
.dc-amt-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(100px,1fr));gap:8px;}
.dc-amt-box{background:#f8fafc;border-radius:8px;padding:10px;text-align:center;border:1px solid #e2e8f0;}
.dab-lbl{display:block;font-size:10px;color:#94a3b8;text-transform:uppercase;}
.dab-val{display:block;font-size:14px;font-weight:700;color:#1e293b;margin-top:3px;}
.green-val{color:#15803d;}.amber-val{color:#b45309;}
.dc-table{width:100%;border-collapse:collapse;font-size:12px;}
.dc-table tr{border-bottom:1px solid #f1f5f9;}.dc-table tr:last-child{border-bottom:none;}
.dc-lbl{padding:7px 0;color:#94a3b8;width:45%;}
.dc-val{padding:7px 0;color:#1e293b;font-weight:600;text-align:right;}
.dc-val.mono{font-family:monospace;font-size:11px;}
.score-row{display:flex;align-items:center;gap:8px;justify-content:flex-end;}
.score-bar-wrap{width:70px;height:5px;background:#f1f5f9;border-radius:3px;overflow:hidden;}
.score-bar{height:100%;border-radius:3px;}
.score-num{font-size:11px;color:#94a3b8;}
.dc-sms-pre{background:#0f172a;border-radius:8px;padding:12px 14px;font-family:'Courier New',monospace;font-size:11px;color:#e2e8f0;line-height:1.7;white-space:pre-wrap;word-break:break-word;max-height:140px;overflow-y:auto;margin:0;}
.dc-error-block{background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:10px 12px;font-size:12px;color:#b91c1c;display:flex;align-items:flex-start;gap:8px;margin-bottom:14px;}
.dc-manual{display:flex;gap:8px;align-items:center;flex-wrap:wrap;}
.dc-footer{padding:10px 16px 14px !important;border-top:1px solid #f1f5f9;display:flex;gap:8px;flex-wrap:wrap;}

/* ── Cancel/Retry dialogs ───────────────────────────────────────────────── */
.cancel-card{border-radius:12px !important;overflow:hidden;}
.cc-head{display:flex;align-items:center;gap:12px;padding:16px 18px;}
.cc-icon-wrap{width:38px;height:38px;background:rgba(255,255,255,.2);border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.cc-title{margin:0;color:#fff;font-size:15px;font-weight:700;}.cc-sub{margin:3px 0 0;color:rgba(255,255,255,.75);font-size:12px;}
.cc-body{padding:16px 18px;}
.cc-row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #f1f5f9;font-size:13px;}
.cc-row:last-of-type{border-bottom:none;}
.cc-lbl{color:#94a3b8;}.cc-val{font-weight:600;color:#1e293b;}
.cc-warning{background:#fff7ed;border:1px solid #fed7aa;border-radius:8px;padding:10px 12px;font-size:12px;color:#92400e;margin-top:12px;line-height:1.5;}
.cc-footer{padding:10px 16px 16px;display:flex;justify-content:flex-end;gap:10px;border-top:1px solid #f1f5f9;}
</style>
